/**
 * @description 买赠赠品展示：库存过滤、按主商品件数计算应赠数量、库存上限裁剪与提示文案
 */

import { getOssUrl } from '~/utils'
import { isGiveawayVODisplayable, isStorePromotionLineStatusAvailable } from '~/utils/product'

/**
 * @param {*} n
 * @returns {number}
 */
const toNum = (n) => {
  const v = Number(n)
  return Number.isFinite(v) ? v : 0
}

/**
 * @description 解析买赠凑单键：同一活动 id + 同一主商品 id
 * @param {Object|null|undefined} line
 * @returns {string|null}
 */
export const resolveGiveawayPromotionProdKey = (line) => {
  if (!line || typeof line !== 'object') return null
  if (Number(line.promotionType ?? line.promotion_type) !== 3) return null
  const promoId = line.promotionId ?? line.promotion_id
  const prodId = line.prodId ?? line.productVO?.prodId
  if (promoId == null || promoId === '' || prodId == null || prodId === '') return null
  return `${String(promoId)}:${String(prodId)}`
}

/**
 * @description 赠品 SKU 可售库存（`skuStock` / `stocks` / `stock`）
 * @param {Object|null|undefined} gift
 * @returns {number|null} 无法解析时返回 null
 */
export const resolveGiveawayGiftSkuStock = (gift) => {
  if (!gift || typeof gift !== 'object') return null
  const raw = gift.skuStock ?? gift.stocks ?? gift.stock
  if (raw === null || raw === undefined || raw === '') return null
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * @description 赠品是否可展示（活动状态可透出且库存大于 0；无库存字段时兼容旧数据）
 * @param {Object|null|undefined} gift
 * @returns {boolean}
 */
export const isGiveawayGiftEligibleForDisplay = (gift) => {
  if (!gift || typeof gift !== 'object') return false
  if (!isGiveawayVODisplayable(gift.status)) return false
  const stock = resolveGiveawayGiftSkuStock(gift)
  if (stock === null) return true
  return stock > 0
}

/**
 * @description 买赠门槛件数
 * @param {Object} line
 * @param {Object} gift
 * @returns {number|null}
 */
export const resolveGiveawayBuyThreshold = (line, gift) => {
  const raw = gift?.buyNum ?? line?.giveawayVO?.buyNum
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * @description 主商品已购件数（同活动同商品多规格合并）
 * @param {Object} line
 * @param {Map<string, number>} [qtyByPromotionProdKey]
 * @returns {number}
 */
export const resolveGiveawayPurchasedQty = (line, qtyByPromotionProdKey) => {
  const key = resolveGiveawayPromotionProdKey(line)
  if (key && qtyByPromotionProdKey instanceof Map) {
    return Math.max(0, Math.trunc(Number(qtyByPromotionProdKey.get(key)) || 0))
  }
  return Math.max(
    0,
    Math.trunc(Number(line?.basketCount ?? line?.prodCount ?? line?.quantity ?? 0))
  )
}

/**
 * @description 赠品应赠数量：优先使用接口 `giveawayProds[].giveawayNum`（后端已算好的总数）；无该字段时再按 buyNum 阶梯推算
 * @param {Object} line
 * @param {Object} gift
 * @param {Map<string, number>} [qtyByPromotionProdKey]
 * @returns {number}
 */
export const resolveGiveawayExpectedGiftQuantity = (line, gift, qtyByPromotionProdKey) => {
  const apiQty = Math.trunc(Number(gift?.giveawayNum) || 0)
  if (apiQty > 0) return apiQty

  const purchased = resolveGiveawayPurchasedQty(line, qtyByPromotionProdKey)
  const threshold = resolveGiveawayBuyThreshold(line, gift)

  if (threshold != null && threshold > 0) {
    const tiers = Math.floor(purchased / threshold)
    if (tiers <= 0) return 0
    return tiers
  }

  return 0
}

/**
 * @description 赠品库存不足时的弱提示文案
 * @param {number} expectedQty
 * @param {number} actualQty
 * @returns {string}
 */
/**
 * @description 赠品展示数量：优先 `actualGiveawayNum`（购物车实际赠送），其次 `giveawayNum`，否则 quantity / prodCount / count
 * @param {Object|null|undefined} gift
 * @returns {number}
 */
export const resolveGiveawayGiftDisplayQuantity = (gift) => {
  const actualQty = Math.trunc(Number(gift?.actualGiveawayNum) || 0)
  if (actualQty > 0) return actualQty
  const apiQty = Math.trunc(Number(gift?.giveawayNum) || 0)
  if (apiQty > 0) return apiQty
  const fallback = Math.trunc(Number(gift?.quantity ?? gift?.prodCount ?? gift?.count) || 0)
  return fallback > 0 ? fallback : 1
}

/**
 * @description 从接口字段解析赠品单价（不含行总价回推）
 * @param {Object|null|undefined} gift
 * @returns {number}
 */
export const resolveGiveawayGiftUnitPriceFromFields = (gift) => {
  if (!gift || typeof gift !== 'object') return 0
  const keys = ['price', 'unitPrice', 'salePrice', 'skuPrice']
  for (const k of keys) {
    const n = Number(gift[k])
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

/**
 * @description 解析赠品展示单价：优先 price 等字段；无有效单价时用行价值÷数量（与订单确认一致）
 * @param {Object|null|undefined} gift
 * @returns {number}
 */
export const resolveGiveawayGiftUnitPrice = (gift) => {
  const fromFields = resolveGiveawayGiftUnitPriceFromFields(gift)
  if (fromFields > 0) return fromFields
  const qty = resolveGiveawayGiftDisplayQuantity(gift)
  if (qty <= 0) return 0
  return resolveGiveawayGiftLineValue(gift) / qty
}

/**
 * @description 单条赠品价值（单价×数量，数量优先 `giveawayNum`；订单详情等 `price=0` 时回退 `giveawayAmount`/`productTotalAmount` 行总价）
 * @param {Object|null|undefined} gift
 * @returns {number}
 */
export const resolveGiveawayGiftLineValue = (gift) => {
  if (!gift || typeof gift !== 'object') return 0
  const qty = resolveGiveawayGiftDisplayQuantity(gift)
  const unitFromFields = resolveGiveawayGiftUnitPriceFromFields(gift)
  const fromUnitQty = unitFromFields > 0 && qty > 0 ? unitFromFields * qty : 0

  const lineTotal = toNum(gift.productTotalAmount)
  if (lineTotal > 0) return lineTotal

  const giveawayAmount = toNum(gift.giveawayAmount)
  if (giveawayAmount > 0) {
    if (fromUnitQty > 0 && giveawayAmount > fromUnitQty + 0.004) return giveawayAmount
    if (fromUnitQty <= 0) return giveawayAmount
  }

  return fromUnitQty
}

/**
 * @description 订单/售后赠品列表项归一化（数量、单价字段与订单确认 `mapOrderConfirmGiveawayProdsFromLine` 对齐）
 * @param {Object|null|undefined} gift
 * @returns {Object|null}
 */
export const normalizeGiveawayGiftForDisplay = (gift) => {
  if (!gift || typeof gift !== 'object') return null
  const qty = resolveGiveawayGiftDisplayQuantity(gift)
  if (qty <= 0) return null
  const unit = resolveGiveawayGiftUnitPriceFromFields(gift)
  const apiGiveawayNum = Math.trunc(Number(gift.giveawayNum) || 0)
  return {
    ...gift,
    prodName: gift.prodName ?? gift.productName ?? gift.name,
    pic: gift.pic ?? gift.image,
    image: gift.pic ? getOssUrl(gift.pic) : gift.image,
    properties: gift.properties ?? gift.skuName ?? gift.spec ?? '',
    giveawayNum: apiGiveawayNum > 0 ? apiGiveawayNum : qty,
    prodCount: qty,
    quantity: qty,
    count: qty,
    ...(unit > 0 ? { price: unit } : {}),
  }
}

/**
 * @description 批量归一化赠品行
 * @param {Array<unknown>} list
 * @returns {Array<Object>}
 */
export const normalizeGiveawayListForDisplay = (list) => {
  if (!Array.isArray(list)) return []
  return list.map((g) => normalizeGiveawayGiftForDisplay(g)).filter(Boolean)
}

export const buildGiveawayGiftStockHint = (expectedQty, actualQty) => {
  const expected = Math.max(0, Math.trunc(Number(expectedQty) || 0))
  const actual = Math.max(0, Math.trunc(Number(actualQty) || 0))
  if (expected <= actual || actual <= 0) return ''
  return `赠品库存仅剩${actual}台，已按库存上限赠送（应赠${expected}台）`
}

/**
 * @description 将 giveawayProds 单项转为购物车赠品行；无库存不输出，赠送数量不超过库存
 * @param {Object} line - V3 行或扁平行
 * @param {Object} gift - giveawayProds 单项
 * @param {Object} [options]
 * @param {number} [options.fallbackActual]
 * @param {Map<string, number>} [options.qtyByPromotionProdKey]
 * @returns {Object|null}
 */
/**
 * @description 订单确认/结算：赠品显隐与数量仅以 `giveawayVO.giveawayProds` 及各项 `giveawayNum` 为准，不做买赠门槛自算
 * @param {Object|null|undefined} line - V3 shopCartItem 或含 `giveawayVO` 的扁平行
 * @returns {Array<Object>}
 */
export const mapOrderConfirmGiveawayProdsFromLine = (line) => {
  const pt = Number(line?.promotionType) || 0
  if (pt > 0 && !isStorePromotionLineStatusAvailable(line)) return []
  const tdcItems = Array.isArray(line?.giveawayItems) ? line.giveawayItems : []
  const legacyProds = Array.isArray(line?.giveawayVO?.giveawayProds)
    ? line.giveawayVO.giveawayProds
    : []
  const prods = tdcItems.length > 0 ? tdcItems : legacyProds
  return prods
    .map((g) => {
      if (!g || typeof g !== 'object') return null
      const configuredQty = Math.max(0, Math.trunc(Number(g.giveawayNum) || 0))
      const actualQty = Math.max(0, Math.trunc(Number(g.actualGiveawayNum) || 0))
      const qty = actualQty > 0 ? actualQty : configuredQty
      if (qty <= 0) return null
      return {
        ...g,
        prodId: g.prodId,
        skuId: g.skuId,
        prodName: g.prodName ?? g.productName,
        skuName: g.skuName,
        pic: g.pic,
        image: getOssUrl(g.pic),
        properties: g.properties ?? g.skuName ?? '',
        giveawayNum: configuredQty > 0 ? configuredQty : qty,
        ...(actualQty > 0 ? { actualGiveawayNum: actualQty } : {}),
        prodCount: qty,
        quantity: qty,
        count: qty,
        price: toNum(g.price),
      }
    })
    .filter(Boolean)
}

export const mapGiveawayProdToCartGift = (line, gift, options = {}) => {
  if (!isGiveawayGiftEligibleForDisplay(gift)) return null

  const apiQty = Math.trunc(Number(gift?.giveawayNum) || 0)
  const actualApiQty = Math.trunc(Number(gift?.actualGiveawayNum) || 0)
  const hasActualGiveawayNum =
    gift?.actualGiveawayNum !== null &&
    gift?.actualGiveawayNum !== undefined &&
    gift?.actualGiveawayNum !== ''

  const qtyMap = options.qtyByPromotionProdKey
  const expectedQty = resolveGiveawayExpectedGiftQuantity(line, gift, qtyMap)

  let displayQty = 0
  let stockLimited = false
  let giveawayStockHint = ''
  const stock = resolveGiveawayGiftSkuStock(gift)

  if (hasActualGiveawayNum) {
    displayQty = Math.max(0, actualApiQty)
    if (displayQty <= 0) return null
    const expectedForHint = apiQty > 0 ? apiQty : expectedQty
    if (expectedForHint > displayQty) {
      stockLimited = true
      giveawayStockHint = buildGiveawayGiftStockHint(expectedForHint, displayQty)
    }
  } else if (expectedQty <= 0) {
    return null
  } else {
    displayQty = expectedQty
    if (apiQty <= 0) {
      displayQty = stock === null ? expectedQty : Math.min(expectedQty, Math.max(0, stock))
      if (displayQty <= 0) return null
      stockLimited = stock !== null && displayQty < expectedQty
      giveawayStockHint = stockLimited ? buildGiveawayGiftStockHint(expectedQty, displayQty) : ''
    }
  }

  const fallbackActual = toNum(options.fallbackActual)

  return {
    basketId:
      gift.giveawayProdId ?? `${line.basketId ?? line.id}_gift_${gift.prodId}_${gift.skuId}`,
    prodId: gift.prodId,
    skuId: gift.skuId,
    modelNo: '',
    name: gift.prodName ?? gift.productName,
    prodName: gift.prodName ?? gift.productName,
    skuName: gift.skuName,
    spec: gift.skuName,
    image: getOssUrl(gift.pic),
    pic: gift.pic,
    giveawayNum: apiQty > 0 ? apiQty : displayQty,
    actualGiveawayNum: hasActualGiveawayNum ? displayQty : undefined,
    quantity: displayQty,
    prodCount: displayQty,
    count: displayQty,
    price: toNum(gift.price),
    actualTotal: fallbackActual,
    stocks: stock ?? undefined,
    expectedGiveawayQty: apiQty > 0 ? apiQty : expectedQty,
    giveawayStockLimited: stockLimited,
    giveawayStockHint,
  }
}
